import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Check, X, Store, ChevronRight, Crown, Shield, Briefcase, FileOutput } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ROLE_META = {
  owner: { label: 'Īpašnieks', icon: Crown },
  co_owner: { label: 'Līdzīpašnieks', icon: Shield },
  employee: { label: 'Darbinieks', icon: Briefcase },
};

export default function BusinessTeamBanner() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [shared, setShared] = useState([]);
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [iv, mn, of] = await Promise.all([
        axios.get(`${API}/business-team/invitations`).then((r) => r.data).catch(() => ({ invitations: [] })),
        axios.get(`${API}/business-team/mine`).then((r) => r.data).catch(() => ({ pages: [] })),
        axios.get(`${API}/business-team/page-offers`).then((r) => r.data).catch(() => ({ offers: [] })),
      ]);
      setInvites(iv.invitations || []);
      setShared(mn.pages || []);
      setOffers(of.offers || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (iv, accept) => {
    const key = `${iv.business_user_id}:${iv.page_id}`;
    setBusy(key);
    try {
      await axios.post(`${API}/business-team/${iv.business_user_id}/${accept ? 'accept' : 'decline'}`, { page_id: iv.page_id });
      toast.success(accept ? 'Pievienojies lapas komandai' : 'Uzaicinājums noraidīts');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās'); }
    finally { setBusy(null); }
  };

  const respondOffer = async (offer, accept) => {
    setBusy(`offer:${offer.id}`);
    try {
      await axios.post(`${API}/business-team/page-offers/${offer.id}/${accept ? 'accept' : 'decline'}`);
      toast.success(accept ? 'Lapa pievienota tavam biznesam' : 'Piedāvājums noraidīts');
      if (accept) { setTimeout(() => window.location.reload(), 900); return; }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās'); setBusy(null); }
  };

  if (invites.length === 0 && shared.length === 0 && offers.length === 0) return null;

  return (
    <div className="mb-8 space-y-3" data-testid="team-banner">
      {/* Page-handover offers (someone wants to give you a page) */}
      {offers.map((o) => (
        <div key={o.id} className="rounded-2xl border border-sky-500/35 bg-sky-500/10 px-5 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`page-offer-row-${o.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center shrink-0"><FileOutput className="w-5 h-5 text-sky-300" /></span>
            <div className="min-w-0">
              <p className="text-sm text-white font-semibold truncate">Lapas nodošana: {o.page_label}</p>
              <p className="text-xs text-white/60">{o.from_name ? `${o.from_name} · ` : ''}vēlas nodot tev šo lapu{o.move_balance ? ' (ar nopelnīto)' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => respondOffer(o, true)} disabled={busy === `offer:${o.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 transition-colors" data-testid={`page-offer-accept-${o.id}`}>
              <Check className="w-3.5 h-3.5" /> Pieņemt lapu
            </button>
            <button onClick={() => respondOffer(o, false)} disabled={busy === `offer:${o.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-50 transition-colors" data-testid={`page-offer-decline-${o.id}`}>
              <X className="w-3.5 h-3.5" /> Noraidīt
            </button>
          </div>
        </div>
      ))}

      {/* Team invites (per page) */}
      {invites.map((iv) => {
        const RM = ROLE_META[iv.role] || ROLE_META.employee;
        const key = `${iv.business_user_id}:${iv.page_id}`;
        return (
          <div key={key} className="rounded-2xl border border-[var(--accent-border)] bg-[var(--accent-soft)] px-5 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`team-invite-row-${iv.page_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-full bg-[var(--accent)]/20 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[var(--accent)]" /></span>
              <div className="min-w-0">
                <p className="text-sm text-white font-semibold truncate">Uzaicinājums: {iv.page_label}</p>
                <p className="text-xs text-white/60">{iv.business_name ? `${iv.business_name} · ` : ''}Loma: {RM.label}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => respond(iv, true)} disabled={busy === key}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 transition-colors" data-testid={`team-invite-accept-${iv.page_id}`}>
                <Check className="w-3.5 h-3.5" /> Pieņemt
              </button>
              <button onClick={() => respond(iv, false)} disabled={busy === key}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-50 transition-colors" data-testid={`team-invite-decline-${iv.page_id}`}>
                <X className="w-3.5 h-3.5" /> Noraidīt
              </button>
            </div>
          </div>
        );
      })}

      {/* Pages shared with me (I'm a team member) */}
      {shared.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="shared-businesses-panel">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Store className="w-4 h-4 text-[var(--accent)]" />
            <p className="font-semibold text-white text-sm">Kopīgotās lapas</p>
          </div>
          <div className="divide-y divide-white/5">
            {shared.map((b) => {
              const RM = ROLE_META[b.role] || ROLE_META.employee;
              const Icon = RM.icon;
              return (
                <button key={`${b.business_user_id}:${b.page_id}`} onClick={() => navigate(`/komanda/${b.business_user_id}/${b.page_id}`)}
                  className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-white/5 transition-colors text-left" data-testid={`shared-business-${b.page_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{b.page_label}</p>
                    <p className="text-[11px] text-white/50 inline-flex items-center gap-1 mt-0.5">{b.name} · <Icon className="w-3 h-3" /> {RM.label}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
